/**
 * ==========================================================================
 *  KALJA - the sip from the bottle
 * ==========================================================================
 *  Press E to trade a brief speed boost for a lurch. Successful sips alternate
 *  left and right, starting left on every run, so players can plan their line
 *  and countersteer. A rejected press during cooldown does not change sides.
 *
 *  The angle within that side, strength and duration still vary with the
 *  simulation step and sip index. These seeded rolls reproduce exactly in the
 *  browser and verifier, without a wall clock or Math.random().
 *
 *  Each sip also raises the live alcohol level, reducing ground grip. This
 *  replaces the old per-sip lurch growth; the starting dose identifies the run.
 * ==========================================================================
 */

export const BEER = {
  /**
   * Minimum seconds between sips, and now the only thing rationing them.
   *
   * Left where it was on purpose. Mashing E buys nothing a press every 0.8 s
   * does not - `boostPerSip` already refills the boost faster than
   * `boostDecayPerSecond` empties it at this cadence - so the cadence is the
   * ceiling and the lurch is the risk. Making the lurch survivable (see
   * `minStrength`) was meant to let a good player hold that cadence through a
   * straight; dropping the cooldown as well would only hand the same boost out
   * for more presses.
   */
  cooldown: 0.8,

  /* --------------------------------------------------------- the reward -- */

  /**
   * Added straight to the vauhti multiplier, on top of `MOMENTUM.max`. This is
   * the only thing in the game that can push the speedometer past the meter's
   * full mark, which is exactly what makes it worth the risk.
   */
  boostPerSip: 0.15,
  /** Ceiling on the stacked boost. 1.9 + 0.3 = 2.2 x 5 m/s = ~40 km/h. */
  maxBoost: 0.3,
  /** Linear bleed. One lone sip is worth having for about two seconds. */
  boostDecayPerSecond: 0.08,

  /* ----------------------------------------------------------- the cost -- */

  /** Length of the lurch, seconds. Rolled between these two. */
  minDuration: 0.35,
  maxDuration: 0.6,
  /**
   * Peak of the lurch, in wish-direction units, before the per-sip growth.
   *
   * Deliberately about a third under a scripted stumble rather than level with
   * one. A sip used to be a coin flip you took once on a straight and then put
   * the bottle away; the point of the bottle is that you keep reaching for it,
   * and a lurch that can end the run is a lurch you only take when you are
   * already safe. This is the number that decides whether the cooldown or the
   * fear is what limits how often E goes down - and it should be the cooldown.
   */
  minStrength: 0.34,
  maxStrength: 0.62,
  /**
   * How much of the lurch also goes straight into velocity, m/s^2 per unit of
   * strength. Kept modest so repeated sips remain steerable.
   */
  impulse: 1.25,
  /**
   * Steering authority while the bottle is at your lip, at full lurch
   * intensity. Not as brutal as `PLAYER.staggerControl` - you are drinking,
   * not being knocked over by a boiled man.
   */
  control: 0.8,
  /** Added to the starting dose per successful sip, capped by alcohol limits. */
  promillePerSip: 0.05,
  /**
   * How much of the shove is allowed to point fore/aft rather than sideways.
   * Kept low on purpose: a lurch that can shove you backwards reads as the
   * game taking the run away from you, and a sideways one reads as booze.
   */
  forwardBias: 0.45,

  /**
   * Mixed into every sip's seed. Changing it re-rolls every sip in the game
   * (and, like any tuning change here, retires the leaderboard).
   */
  seed: 0x4b414c4a,
} as const;
